import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import GroupHeader from "./GroupHeader";
import { RenderableGroup } from "../../../../utils/budget-groups";

export const GROUP_BODY_PREFIX = "group-body-";

/** Id do droppable que cobre o corpo do agrupamento (inclusive quando vazio). */
export const groupBodyDroppableId = (groupId: string) =>
  `${GROUP_BODY_PREFIX}${groupId}`;

type PropTypes = {
  group: RenderableGroup;
  total: number;
  /** Ids dos sortables das linhas de fornecedor deste agrupamento, na ordem exibida. */
  rowIds: string[];
  readOnly: boolean;
  /** Com o formulário aberto o botão tracejado dá lugar a ele, como no protótipo. */
  hideAddButton?: boolean;
  /** Conteúdo abaixo das linhas — hoje o formulário "Novo fornecedor em …". */
  footer?: React.ReactNode;
  onRename: (name: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onAddSupplier: () => void;
  children: React.ReactNode;
};

/**
 * Uma caixa de agrupamento: cabeçalho, as linhas de fornecedor que já existem e
 * o rodapé "+ Adicionar fornecedor".
 *
 * O `BudgetSupplier` NÃO é alterado por este componente — o agrupamento envolve,
 * não substitui. A linha do fornecedor, seus ícones de categoria e seus totais
 * continuam sendo renderizados pelo mesmo código de sempre.
 *
 * O `SortableContext` das linhas fica AQUI (um por agrupamento), mas o
 * `DndContext` é único e vive em `GroupedSupplierRows` — é isso que permite
 * arrastar um fornecedor de um agrupamento para outro.
 */
const BudgetGroupBlock: React.FC<PropTypes> = ({
  group,
  total,
  rowIds,
  readOnly,
  hideAddButton,
  footer,
  onRename,
  onDuplicate,
  onDelete,
  onAddSupplier,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: group.id,
    // "Sem agrupamento" é pseudo: não pode ser arrastado nem reordenado.
    disabled: !group.isReal || readOnly,
    data: { kind: "group" },
  });

  // Alvo de soltura para o fornecedor arrastado — inclusive com o agrupamento
  // vazio, que não tem nenhuma linha sob a qual soltar.
  const { setNodeRef: setBodyRef, isOver: isBodyOver } = useDroppable({
    id: groupBodyDroppableId(group.id),
    data: { kind: "group-body", groupId: group.id },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        mt: 1.5,
        transform: CSS.Transform.toString(transform),
        transition,
        // Mesmo feedback dos outros três níveis de arrasto da tela.
        opacity: isDragging ? 0.5 : 1,
        outline: isOver && !isDragging ? "2px dashed #1361A4" : "none",
        outlineOffset: "2px",
        borderRadius: "6px",
      }}
    >
      <GroupHeader
        name={group.name}
        total={total}
        isReal={group.isReal}
        readOnly={readOnly}
        dragAttributes={attributes}
        dragListeners={listeners}
        onRename={onRename}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
      />

      <Box
        ref={setBodyRef}
        sx={{
          backgroundColor: isBodyOver ? "#EEF4FB" : "transparent",
          borderRadius: "6px",
          transition: "background-color 0.15s",
          pb: rowIds.length === 0 ? 0 : 0.5,
        }}
      >
        {rowIds.length === 0 ? (
          <Box
            sx={{
              px: 1.5,
              py: 1.5,
              fontSize: 13,
              fontStyle: "italic",
              color: "#888",
            }}
          >
            Nenhum fornecedor neste agrupamento ainda.
          </Box>
        ) : (
          <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
            {children}
          </SortableContext>
        )}
      </Box>

      {footer}

      {group.isReal && !readOnly && !hideAddButton && (
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddSupplier}
          sx={{
            fontSize: "13px",
            mt: 0.5,
            color: "#1361A4",
            backgroundColor: "transparent",
            border: "1px dashed #9CC2E5",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#EEF4FB",
              border: "1px dashed #1361A4",
            },
            "& .MuiButton-startIcon": { margin: 0, mr: 0.5 },
          }}
        >
          {`Adicionar fornecedor em ${group.name}`}
        </Button>
      )}
    </Box>
  );
};

export default BudgetGroupBlock;
